import { z } from 'zod';

/**
 * Schemas Zod du module Bâtiments (Phase 8 — US-BAT-01 à 07 + US-TR-12).
 *
 * Hiérarchie : Bâtiment → Étage → Salle. Documents et réservations
 * rattachables à n'importe lequel des trois niveaux.
 */

// ─── Constantes ─────────────────────────────────────────────────────────────

export const BATIMENT_TYPES = [
  'principal',
  'annexe',
  'technique',
  'administratif',
  'operationnel',
] as const;
export type BatimentType = (typeof BATIMENT_TYPES)[number];

export const BATIMENT_STATUTS = [
  'en_service',
  'en_travaux',
  'ferme_temporairement',
  'en_projet',
  'desaffecte',
] as const;
export type BatimentStatut = (typeof BATIMENT_STATUTS)[number];

export const ETAGE_STATUTS = [
  'operationnel',
  'acces_restreint',
  'en_renovation',
  'ferme',
  'acces_interdit',
] as const;
export type EtageStatut = (typeof ETAGE_STATUTS)[number];

export const SALLE_TYPES = [
  'accueil',
  'activite',
  'technique',
  'bureau',
  'stockage',
  'circulation',
  'autre',
] as const;
export type SalleType = (typeof SALLE_TYPES)[number];

export const SALLE_STATUTS = [
  'disponible',
  'occupee',
  'en_maintenance',
  'en_nettoyage',
  'hors_service',
  'reservee',
  'en_travaux',
] as const;
export type SalleStatut = (typeof SALLE_STATUTS)[number];

// ─── Bâtiments ──────────────────────────────────────────────────────────────

export const createBatimentSchema = z.object({
  code: z.string().min(1).max(50),
  nom: z.string().min(1).max(255),
  type: z.enum(BATIMENT_TYPES),
  adresse: z.string().max(1000).optional().nullable(),
  surface_totale: z.coerce.number().min(0).optional().nullable(),
  nombre_etages: z.coerce.number().int().min(0).default(0),
  statut: z.enum(BATIMENT_STATUTS).default('en_service'),
  responsable_id: z.string().uuid().optional().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateBatimentInput = z.infer<typeof createBatimentSchema>;

export const updateBatimentSchema = createBatimentSchema.partial();
export type UpdateBatimentInput = z.infer<typeof updateBatimentSchema>;

export const listBatimentsQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  type: z.enum(BATIMENT_TYPES).optional(),
  statut: z.enum(BATIMENT_STATUTS).optional(),
  sort_by: z.enum(['code', 'nom', 'type', 'statut', 'surface_totale', 'created_at']).default('nom'),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListBatimentsQuery = z.infer<typeof listBatimentsQuerySchema>;

// ─── Étages ─────────────────────────────────────────────────────────────────

export const createEtageSchema = z.object({
  batiment_id: z.string().uuid(),
  code: z.string().min(1).max(50),
  numero: z.string().min(1).max(20),
  nom: z.string().max(255).optional().nullable(),
  surface_totale: z.coerce.number().min(0).optional().nullable(),
  statut: z.enum(ETAGE_STATUTS).default('operationnel'),
  responsable_id: z.string().uuid().optional().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateEtageInput = z.infer<typeof createEtageSchema>;

export const updateEtageSchema = createEtageSchema.partial();
export type UpdateEtageInput = z.infer<typeof updateEtageSchema>;

// ─── Salles ─────────────────────────────────────────────────────────────────

export const createSalleSchema = z.object({
  etage_id: z.string().uuid(),
  code: z.string().min(1).max(50),
  numero_porte: z.string().max(20).optional().nullable(),
  nom: z.string().min(1).max(255),
  type: z.enum(SALLE_TYPES),
  surface: z.coerce.number().min(0).optional().nullable(),
  statut: z.enum(SALLE_STATUTS).default('disponible'),
  service_id: z.string().uuid().optional().nullable(),
  responsable_id: z.string().uuid().optional().nullable(),
  est_reservable: z.boolean().default(false),
  duree_max_reservation: z.coerce.number().int().min(0).optional().nullable(),
  preavis_requis: z.coerce.number().int().min(0).optional().nullable(),
  validation_requise: z.boolean().default(false),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateSalleInput = z.infer<typeof createSalleSchema>;

export const updateSalleSchema = createSalleSchema.partial();
export type UpdateSalleInput = z.infer<typeof updateSalleSchema>;

export const listSallesQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  batiment_id: z.string().uuid().optional(),
  etage_id: z.string().uuid().optional(),
  service_id: z.string().uuid().optional(),
  type: z.enum(SALLE_TYPES).optional(),
  statut: z.enum(SALLE_STATUTS).optional(),
  est_reservable: z.coerce.boolean().optional(),
  sort_by: z.enum(['code', 'nom', 'type', 'statut', 'surface']).default('code'),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListSallesQuery = z.infer<typeof listSallesQuerySchema>;

// ─── Affectation salle → service (US-BAT-04) ───────────────────────────────

export const affectSalleSchema = z.object({
  service_id: z.string().uuid().nullable(),
  /** 'principal' | 'secondaire' | 'partage' — pour l'historique d'affectation. */
  type_utilisation: z.enum(['principal', 'secondaire', 'partage']).default('principal'),
});
export type AffectSalleInput = z.infer<typeof affectSalleSchema>;

// ─── Réservations (US-BAT-05) ──────────────────────────────────────────────

export const RESERVATION_STATUTS = [
  'demande',
  'en_attente',
  'validee',
  'confirmee',
  'annulee',
] as const;
export type ReservationStatut = (typeof RESERVATION_STATUTS)[number];

export const RECURRENCE_TYPES = ['quotidienne', 'hebdomadaire', 'mensuelle'] as const;
export type RecurrenceType = (typeof RECURRENCE_TYPES)[number];

export const reservationRecurrenceSchema = z.object({
  type_recurrence: z.enum(RECURRENCE_TYPES),
  jours_semaine: z.array(z.number().int().min(0).max(6)).default([]),
  frequence: z.coerce.number().int().min(1).default(1),
  date_fin_recurrence: z.string().date(),
});
export type ReservationRecurrence = z.infer<typeof reservationRecurrenceSchema>;

export const createReservationSchema = z
  .object({
    salle_id: z.string().uuid(),
    objet: z.string().min(1).max(255),
    date_debut: z.string().datetime(),
    date_fin: z.string().datetime(),
    service_id: z.string().uuid().optional().nullable(),
    custom_fields: z.record(z.string(), z.unknown()).optional(),
    recurrence: reservationRecurrenceSchema.optional(),
  })
  .refine((d) => new Date(d.date_fin) > new Date(d.date_debut), {
    message: 'date_fin doit être strictement postérieure à date_debut',
    path: ['date_fin'],
  });
export type CreateReservationInput = z.infer<typeof createReservationSchema>;

export const listReservationsQuerySchema = z.object({
  salle_id: z.string().uuid().optional(),
  batiment_id: z.string().uuid().optional(),
  utilisateur_id: z.string().uuid().optional(),
  statut: z.enum(RESERVATION_STATUTS).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  sort_by: z.enum(['date_debut', 'date_fin', 'objet', 'statut']).default('date_debut'),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListReservationsQuery = z.infer<typeof listReservationsQuerySchema>;

export const validateReservationSchema = z.object({
  approve: z.boolean(),
  motif_refus: z.string().max(1000).optional().nullable(),
});
export type ValidateReservationInput = z.infer<typeof validateReservationSchema>;

// ─── Documents (US-BAT-06 + US-TR-12) ──────────────────────────────────────

export const DOCUMENT_ENTITY_TYPES = [
  'batiment',
  'etage',
  'salle',
  'service',
  'equipement',
  'intervention',
  'etalonnage',
  'user',
] as const;
export type DocumentEntityType = (typeof DOCUMENT_ENTITY_TYPES)[number];

export const DOCUMENT_TYPES = [
  'plan_architectural',
  'schema_technique',
  'plan_evacuation',
  'certificat',
  'rapport_controle',
  'photo',
  'contrat',
  'manuel',
  'historique_travaux',
  'autre',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const CONFIDENTIALITE_LEVELS = ['public', 'interne', 'restreint', 'confidentiel'] as const;
export type ConfidentialiteLevel = (typeof CONFIDENTIALITE_LEVELS)[number];

export const createDocumentMetadataSchema = z.object({
  entity_type: z.enum(DOCUMENT_ENTITY_TYPES),
  entity_id: z.string().uuid(),
  nom: z.string().min(1).max(255),
  type_document: z.enum(DOCUMENT_TYPES),
  niveau_confidentialite: z.enum(CONFIDENTIALITE_LEVELS).default('interne'),
  date_validite_debut: z.string().date().optional().nullable(),
  date_validite_fin: z.string().date().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateDocumentMetadataInput = z.infer<typeof createDocumentMetadataSchema>;

export const updateDocumentSchema = z.object({
  nom: z.string().min(1).max(255).optional(),
  type_document: z.enum(DOCUMENT_TYPES).optional(),
  niveau_confidentialite: z.enum(CONFIDENTIALITE_LEVELS).optional(),
  date_validite_debut: z.string().date().nullable().optional(),
  date_validite_fin: z.string().date().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;

export const listDocumentsQuerySchema = z.object({
  entity_type: z.enum(DOCUMENT_ENTITY_TYPES).optional(),
  entity_id: z.string().uuid().optional(),
  type_document: z.enum(DOCUMENT_TYPES).optional(),
  niveau_confidentialite: z.enum(CONFIDENTIALITE_LEVELS).optional(),
  search: z.string().max(255).optional(),
  /** Filtre expirables (date_validite_fin <= maintenant + N jours). */
  expiring_in_days: z.coerce.number().int().min(0).optional(),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListDocumentsQuery = z.infer<typeof listDocumentsQuerySchema>;

export const approveDocumentSchema = z.object({
  approve: z.boolean(),
  commentaire: z.string().max(2000).optional().nullable(),
});
export type ApproveDocumentInput = z.infer<typeof approveDocumentSchema>;

// ─── Historique (US-BAT-07) ────────────────────────────────────────────────

export const HISTORIQUE_ENTITY_TYPES = [
  'batiment',
  'etage',
  'salle',
  'service',
  'equipement',
] as const;
export type HistoriqueEntityType = (typeof HISTORIQUE_ENTITY_TYPES)[number];

export const listHistoriqueQuerySchema = z.object({
  entity_type: z.enum(HISTORIQUE_ENTITY_TYPES),
  entity_id: z.string().uuid(),
  /** 'all' | 'statuts' | 'affectations' */
  kind: z.enum(['all', 'statuts', 'affectations']).default('all'),
});
export type ListHistoriqueQuery = z.infer<typeof listHistoriqueQuerySchema>;
